import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiMapPin,
  FiZoomIn,
  FiZoomOut,
  FiRotateCcw,
  FiPlay,
} from 'react-icons/fi';
import api from '../../../Api.js';
import './PopularDestinations.css';

const API_TOKEN =
  '50cd682741a5119f4fc86830fe99960aaad2fb302377586ef753599def7af8b5';

/* ---------- YouTube helpers ---------- */
const extractYouTubeId = (url) => {
  if (!url) return null;
  const regExp = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?#]+)/;
  const match = url.match(regExp);
  return match ? match[1] : null;
};

const getYouTubeEmbedUrl = (url, autoplay = false) => {
  const id = extractYouTubeId(url);
  if (!id) return null;
  return `https://www.youtube.com/embed/${id}${autoplay ? '?autoplay=1&rel=0' : '?rel=0'}`;
};

const getYouTubeThumbnail = (url) => {
  const id = extractYouTubeId(url);
  if (!id) return null;
  return `https://img.youtube.com/vi/${id}/maxresdefault.jpg`;
};

const PopularDestinations = () => {
  const [popularDestinations, setPopularDestination] = useState([]);
  const [loading, setLoading] = useState(true);

  /* ---------- Lightbox state ---------- */
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [mediaItems, setMediaItems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isFetching, setIsFetching] = useState(false);
  const [destinationName, setDestinationName] = useState('');
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const scrollRef = useRef(null);

  /* ---------- Fetch destinations ---------- */
  useEffect(() => {
    const getDestinations = async () => {
      try {
        const response = await api.get('/destinations', {
          headers: { 'X-API-TOKEN': API_TOKEN },
        });
        if (response.data.status) {
          setPopularDestination(response.data.data);
        }
      } catch (error) {
        console.log('API Error:', error);
      } finally {
        setLoading(false);
      }
    };
    getDestinations();
  }, []);

  /* ---------- Horizontal scroll ---------- */
  const handleScroll = (direction) => {
    if (scrollRef.current) {
      const amount = direction === 'left' ? -360 : 360;
      scrollRef.current.scrollBy({ left: amount, behavior: 'smooth' });
    }
  };

  /* ---------- Lightbox open ---------- */
  const openLightbox = async (destination) => {
    setIsFetching(true);
    setDestinationName(destination.name);
    setZoomLevel(1);
    setIsVideoPlaying(false);

    try {
      const response = await api.get(`/destinations/${destination.id}`, {
        headers: { 'X-API-TOKEN': API_TOKEN },
      });

      const data = response.data.data;
      const cover = destination.cover_photo || '';
      const photos = data.photos || [];
      const videos = data.videos || [];

      const items = [];
      if (cover) items.push({ type: 'image', url: cover, title: 'Cover' });
      photos.forEach((url) => {
        if (url) items.push({ type: 'image', url });
      });
      videos.forEach((url) => {
        if (url) items.push({ type: 'video', url });
      });

      setMediaItems(items);
      setCurrentIndex(0);
      setLightboxOpen(true);
    } catch (error) {
      console.error('Failed to fetch destination details:', error);
      const cover = destination.cover_photo || '';
      if (cover) {
        setMediaItems([{ type: 'image', url: cover, title: 'Cover' }]);
        setCurrentIndex(0);
        setLightboxOpen(true);
      }
    } finally {
      setIsFetching(false);
    }
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setMediaItems([]);
    setZoomLevel(1);
    setIsVideoPlaying(false);
  };

  const goPrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? mediaItems.length - 1 : prev - 1));
    setZoomLevel(1);
    setIsVideoPlaying(false);
  };

  const goNext = () => {
    setCurrentIndex((prev) => (prev === mediaItems.length - 1 ? 0 : prev + 1));
    setZoomLevel(1);
    setIsVideoPlaying(false);
  };

  const handleZoomIn = () => setZoomLevel((prev) => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoomLevel((prev) => Math.max(prev - 0.25, 0.5));
  const handleResetZoom = () => setZoomLevel(1);
  const handlePlayVideo = () => setIsVideoPlaying(true);

  /* ---------- Keyboard ---------- */
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, mediaItems]);

  useEffect(() => {
    document.body.style.overflow = lightboxOpen ? 'hidden' : '';
    return () => (document.body.style.overflow = '');
  }, [lightboxOpen]);

  /* ---------- Render media ---------- */
  const renderMediaItem = (item, index) => {
    if (index !== currentIndex) return null;

    if (item.type === 'video') {
      const embedUrl = getYouTubeEmbedUrl(item.url, isVideoPlaying);
      const thumbnail = getYouTubeThumbnail(item.url);

      return (
        <div className="pd-lb-media pd-lb-media-video">
          {isVideoPlaying && embedUrl ? (
            <iframe
              src={embedUrl}
              className="pd-lb-video-iframe"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <button
              type="button"
              className="pd-lb-video-thumb"
              onClick={handlePlayVideo}
              aria-label="Play video"
            >
              {thumbnail ? (
                <img
                  src={thumbnail}
                  alt="Video thumbnail"
                  className="pd-lb-video-thumb-img"
                />
              ) : (
                <span className="pd-lb-video-placeholder" aria-hidden="true">
                  🎬
                </span>
              )}
              <span className="pd-lb-play">
                <FiPlay />
              </span>
              <span className="pd-lb-video-label">Tap to play</span>
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="pd-lb-media pd-lb-media-image">
        <img
          src={item.url}
          alt={item.title || `Image ${index + 1}`}
          className="pd-lb-image"
          style={{ transform: `scale(${zoomLevel})` }}
          draggable={false}
        />
      </div>
    );
  };

  /* ---------- Loading ---------- */
  if (loading) {
    return (
      <section className="pd-section">
        <span className="pd-orb pd-orb-a" aria-hidden="true" />
        <span className="pd-orb pd-orb-b" aria-hidden="true" />
        <div className="pd-shell">
          <header className="pd-header">
            <div className="pd-header-text">
              <h2 className="pd-title">Popular Destinations</h2>
            </div>
          </header>
          <div className="pd-rail" aria-busy="true">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="pd-skeleton" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  const currentItem = mediaItems[currentIndex] || {};
  const isImage = currentItem.type === 'image';
  const totalItems = mediaItems.length;

  return (
    <section className="pd-section">
      <span className="pd-orb pd-orb-a" aria-hidden="true" />
      <span className="pd-orb pd-orb-b" aria-hidden="true" />

      <div className="pd-shell">
        {/* ---------- Header ---------- */}
        <motion.header
          className="pd-header"
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
        >
          <div className="pd-header-text">
            <h2 className="pd-title">Popular Destinations</h2>
            <p className="pd-subtitle">
              Timeless locations for unforgettable celebrations
            </p>
          </div>

          <div className="pd-controls">
            <button
              type="button"
              className="pd-ctrl"
              onClick={() => handleScroll('left')}
              aria-label="Scroll left"
            >
              <FiChevronLeft />
            </button>
            <button
              type="button"
              className="pd-ctrl"
              onClick={() => handleScroll('right')}
              aria-label="Scroll right"
            >
              <FiChevronRight />
            </button>
          </div>
        </motion.header>

        {/* ---------- Rail ---------- */}
        <div className="pd-rail" ref={scrollRef}>
          {popularDestinations.map((item, index) => {
            const citiesList = item.cities?.slice(0, 3).map((c) => c.name).join(', ');
            const hasMore = item.cities?.length > 3;

            return (
              <motion.article
                key={item.id}
                className="pd-card"
                onClick={() => openLightbox(item)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openLightbox(item);
                  }
                }}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.5, delay: Math.min(index, 6) * 0.08 }}
              >
                <div className="pd-card-media">
                  <img
                    src={item.cover_photo}
                    alt={item.name}
                    className="pd-card-img"
                    loading="lazy"
                  />
                  <span className="pd-card-veil" aria-hidden="true" />

                  <span className="pd-card-body">
                    <span className="pd-card-badge">
                      <FiMapPin aria-hidden="true" />
                    </span>
                    <h3 className="pd-card-title">{item.name}</h3>
                    {citiesList && (
                      <p className="pd-card-subtitle">
                        {citiesList}
                        {hasMore ? '…' : ''}
                      </p>
                    )}
                  </span>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>

      {/* ============================================================
          Lightbox
         ============================================================ */}
      {lightboxOpen && (
        <div className="pd-lb-overlay" onClick={closeLightbox}>
          <div className="pd-lb-content" onClick={(e) => e.stopPropagation()}>
            {/* Close */}
            <button
              type="button"
              className="pd-lb-close"
              onClick={closeLightbox}
              aria-label="Close"
            >
              <FiX />
            </button>

            {/* Nav arrows */}
            {totalItems > 1 && (
              <>
                <button
                  type="button"
                  className="pd-lb-nav pd-lb-nav-prev"
                  onClick={goPrev}
                  aria-label="Previous"
                >
                  <FiChevronLeft />
                </button>
                <button
                  type="button"
                  className="pd-lb-nav pd-lb-nav-next"
                  onClick={goNext}
                  aria-label="Next"
                >
                  <FiChevronRight />
                </button>
              </>
            )}

            {/* Media */}
            <div className="pd-lb-stage">
              {isFetching ? (
                <div className="pd-lb-loader">Loading media…</div>
              ) : (
                renderMediaItem(currentItem, currentIndex)
              )}
            </div>

            {/* Bottom controls */}
            <div className="pd-lb-controls">
              <div className="pd-lb-left">
                {totalItems > 0 && (
                  <span className="pd-lb-counter">
                    <strong>{currentIndex + 1}</strong>
                    <span className="pd-lb-counter-sep">/</span>
                    {totalItems}
                  </span>
                )}
                {currentItem.type === 'video' && (
                  <span className="pd-lb-chip">🎬 Video</span>
                )}
                {currentItem.type === 'image' && (
                  <span className="pd-lb-chip">📷 Photo</span>
                )}
              </div>

              <div className="pd-lb-right">
                {isImage && (
                  <div className="pd-lb-zoom">
                    <button onClick={handleZoomOut} aria-label="Zoom out">
                      <FiZoomOut />
                    </button>
                    <span className="pd-lb-zoom-value">
                      {Math.round(zoomLevel * 100)}%
                    </span>
                    <button onClick={handleZoomIn} aria-label="Zoom in">
                      <FiZoomIn />
                    </button>
                    <button onClick={handleResetZoom} aria-label="Reset zoom">
                      <FiRotateCcw />
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  className="pd-lb-cta"
                  onClick={() => {
                    window.location.href = `/honeymoon/packages?location=${encodeURIComponent(
                      destinationName
                    )}`;
                  }}
                >
                  View All Packages
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PopularDestinations;